import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../config/axios';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);
  const refreshTimeoutRef = useRef(null);

  const refreshNotifications = useCallback(async (force = false) => {
    // Éviter les appels multiples dans un court laps de temps
    const now = Date.now();
    if (!force && now - lastFetchRef.current < 30000) { // 30 secondes de cooldown
      return;
    }

    // Annuler le timeout précédent s'il existe
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Créer un nouveau timeout pour éviter les appels multiples
    refreshTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get('/api/analysed-files');
        const data = response.data?.data || [];
        setNotifications(data);
        setUnreadCount(data.filter(n => n.isNew === true || n.is_new === true).length);
        lastFetchRef.current = Date.now();
      } catch (error) {
        console.error('Erreur lors du chargement des notifications:', error);
        setNotifications([]);
        setUnreadCount(0);
      } finally {
        setLoading(false);
      }
    }, 500); // Debounce de 500ms
  }, []);

  // Charger les notifications au démarrage
  useEffect(() => {
    refreshNotifications(true);
    
    // Nettoyer le timeout lors du démontage
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [refreshNotifications]);

  return (
    <NotificationContext.Provider value={{
      notifications: notifications || [],
      unreadCount,
      refreshNotifications,
      loading
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
} 